import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarPlus, Download, Timer } from "lucide-react";
import { API, http, useApi, errorText } from "@/lib/api";
import { toast } from "sonner";
import { useT } from "@/lib/i18n";
import { fmtDate } from "@/lib/utils";

const pad = (n) => String(Math.max(0, n)).padStart(2, "0");

export const googleCalUrl = (f) => {
  const s = new Date(f.kickoff);
  const e = new Date(s.getTime() + 2 * 3600 * 1000);
  const fmt = (d) => d.toISOString().replace(/[-:]|\.\d{3}/g, "");
  const p = new URLSearchParams({ action: "TEMPLATE", text: `${f.home} vs ${f.away}`, dates: `${fmt(s)}/${fmt(e)}`, details: `${f.competition || ""} — via MiniSports`, location: f.venue || "" });
  return `https://calendar.google.com/calendar/render?${p}`;
};

export const AddToCalendar = ({ f, compact = false, dark = false }) => {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  return (
    <div className="relative" onMouseLeave={() => setOpen(false)}>
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid={`add-to-calendar-${f.id}`} aria-label={t("Add to calendar")}
        className={`inline-flex items-center gap-1.5 rounded-full text-xs font-bold transition-colors ${compact ? "h-8 w-8 justify-center" : "px-3 py-1.5"} ${dark ? "border border-white/30 bg-white/10 text-white hover:bg-[#F5B301] hover:text-[#0B1120]" : "border border-[#047857]/30 bg-[#047857]/10 text-[#047857] hover:bg-[#047857] hover:text-white"}`}>
        <CalendarPlus size={14} />{!compact && t("Remind me")}
      </button>
      {open && (
        <div className="absolute right-0 z-30 mt-1 w-48 overflow-hidden rounded-xl border border-slate-200 bg-white text-left shadow-xl" data-testid={`calendar-menu-${f.id}`}>
          <a href={googleCalUrl(f)} target="_blank" rel="noreferrer" data-testid={`calendar-google-${f.id}`} className="flex items-center gap-2 px-3 py-2.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"><CalendarPlus size={14} className="text-[#1D4ED8]" /> Google Calendar</a>
          <a href={`${API}/fixtures/${f.id}/calendar.ics`} data-testid={`calendar-ics-${f.id}`} className="flex items-center gap-2 border-t border-slate-100 px-3 py-2.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"><Download size={14} className="text-[#047857]" /> Apple / Outlook (.ics)</a>
        </div>
      )}
    </div>
  );
};

const useCountdown = (iso) => {
  const [left, setLeft] = useState(() => new Date(iso) - Date.now());
  useEffect(() => {
    const id = setInterval(() => setLeft(new Date(iso) - Date.now()), 1000);
    return () => clearInterval(id);
  }, [iso]);
  const s = Math.floor(left / 1000);
  return { left, d: Math.floor(s / 86400), h: Math.floor((s % 86400) / 3600), m: Math.floor((s % 3600) / 60), sec: s % 60 };
};

export const LionsLastResult = ({ f, refreshing }) => {
  const { t } = useT();
  return (
    <div className="rise rise-4 mt-8 inline-flex max-w-full flex-col gap-3 rounded-2xl border border-white/15 bg-[#0B1120]/60 p-4 backdrop-blur-md sm:flex-row sm:items-center sm:gap-6" data-testid="lions-last-result">
      <div className="min-w-0">
        <p className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.3em] text-[#F5B301]"><Timer size={12} /> {t("Last Lions result")}</p>
        {f ? (
          <>
            <p className="mt-1 truncate text-sm font-bold text-white" data-testid="lions-last-result-match">
              <span className={f.home === "Cameroon" ? "text-emerald-300" : ""}>{f.home}</span> <span className="mx-1 rounded bg-white/15 px-1.5 font-display">{f.home_score} - {f.away_score}</span> <span className={f.away === "Cameroon" ? "text-emerald-300" : ""}>{f.away}</span>
            </p>
            <p className="truncate text-xs text-slate-300">{f.competition} · {fmtDate(f.kickoff, false)}</p>
          </>
        ) : <p className="mt-1 text-sm text-slate-300">{t("No recent result available.")}</p>}
      </div>
      <div className="flex items-center gap-3 text-xs">
        <span className="rounded-full border border-white/20 px-3 py-1.5 font-semibold text-slate-200" data-testid="lions-next-tba">{refreshing ? t("Checking next fixture…") : t("Next fixture: to be announced")}</span>
        <Link to="/live-scores" className="font-bold text-[#F5B301] hover:underline" data-testid="lions-countdown-live-link">{t("Match centre")} →</Link>
      </div>
    </div>
  );
};

const voterId = () => {
  let v = localStorage.getItem("ms_voter");
  if (!v) { v = Math.random().toString(36).slice(2) + Date.now().toString(36); localStorage.setItem("ms_voter", v); }
  return v;
};

export const MatchPoll = ({ f }) => {
  const { t } = useT();
  const voter = voterId();
  const { data, refetch } = useApi("poll", `/polls/${f.id}?voter=${voter}`, { refetchInterval: 20000 });
  const [busy, setBusy] = useState(false);
  const cast = async (choice) => {
    setBusy(true);
    try { await http.post(`/polls/${f.id}/vote`, { choice, voter }); await refetch(); } catch (e) { toast.error(errorText(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const counts = data?.counts || { home: 0, draw: 0, away: 0 };
  const total = data?.total || 0;
  const pct = (k) => (total ? Math.round((counts[k] / total) * 100) : 0);
  const opts = [["home", f.home], ["draw", t("Draw")], ["away", f.away]];
  return (
    <div className="mt-3 w-full border-t border-white/10 pt-3" data-testid="match-poll">
      <p className="flex items-center justify-between text-[11px] font-bold uppercase tracking-[0.25em] text-[#F5B301]"><span>{t("Fan prediction")}</span><span className="text-slate-300 normal-case tracking-normal" data-testid="poll-total">{total} {t("votes")}</span></p>
      <div className="mt-2 grid grid-cols-3 gap-2">
        {opts.map(([k, label]) => {
          const mine = data?.my_vote === k;
          return (
            <button key={k} onClick={() => cast(k)} disabled={busy} data-testid={`poll-option-${k}`}
              className={`relative overflow-hidden rounded-lg border px-2 py-2 text-left text-xs font-bold transition-colors ${mine ? "border-[#F5B301] bg-[#F5B301]/15 text-white" : "border-white/15 bg-white/5 text-slate-200 hover:border-white/40"}`}>
              <span className="absolute inset-y-0 left-0 bg-[#047857]/50 transition-all duration-700" style={{ width: `${pct(k)}%` }} />
              <span className="relative block truncate">{label}</span>
              <span className="relative block font-display text-lg leading-none" data-testid={`poll-pct-${k}`}>{pct(k)}%</span>
            </button>
          );
        })}
      </div>
      {data?.my_vote && <p className="mt-1.5 text-[11px] text-slate-300" data-testid="poll-my-vote">{t("Your pick")}: {opts.find(([k]) => k === data.my_vote)?.[1]} · {t("tap another option to change")}</p>}
    </div>
  );
};

export const LionsCountdown = ({ f }) => {
  const { t } = useT();
  const { left, d, h, m, sec } = useCountdown(f.kickoff);
  const started = left <= 0;
  const Team = ({ n }) => <span className={n === "Cameroon" ? "text-emerald-300" : ""}>{n}</span>;
  return (
    <div className="rise rise-4 mt-8 inline-flex max-w-full flex-col rounded-2xl border border-white/15 bg-[#0B1120]/60 p-4 backdrop-blur-md" data-testid="lions-countdown">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-6">
      <div className="min-w-0">
        <p className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.3em] text-[#F5B301]"><Timer size={12} /> {started ? t("Lions in action now") : t("Next Lions kick-off")}</p>
        <p className="mt-1 truncate text-sm font-bold text-white" data-testid="lions-countdown-match">
          <Team n={f.home} /> vs <Team n={f.away} />
        </p>
        <p className="truncate text-xs text-slate-300">{f.competition} · {fmtDate(f.kickoff)}{f.venue ? ` · ${f.venue}` : ""}</p>
      </div>
      {!started && (
        <div className="flex gap-2" data-testid="lions-countdown-timer">
          {[[d, "Days"], [h, "Hrs"], [m, "Min"], [sec, "Sec"]].map(([v, l]) => (
            <div key={l} className="min-w-[52px] rounded-lg bg-white/10 px-2 py-1.5 text-center">
              <p className="font-display text-2xl font-bold leading-none text-white tabular-nums">{pad(v)}</p>
              <p className="text-[10px] uppercase tracking-wider text-slate-300">{t(l)}</p>
            </div>
          ))}
        </div>
      )}
      <div className="flex items-center gap-2">
        <AddToCalendar f={f} dark />
        <Link to="/live-scores" className="text-xs font-bold text-[#F5B301] hover:underline" data-testid="lions-countdown-live-link">{t("Match centre")} →</Link>
      </div>
      </div>
      <MatchPoll f={f} />
    </div>
  );
};
